import { CircularProgress, Paper } from "@mui/material";

import _ from "lodash";
import { SmartTable } from "src/components/SmartTable";
import { BeaconIcon } from "src/components/StyledComponents";
import { formatDateString } from "src/utility/utilityFunctions";

import Tags from "@features/ui/Tags";
import { Order } from "@models/Order";
import { OrderVariant } from "@models/OrderVariant";

import CompareCosts from "../CompareCosts";
import {
  displayToInventoryAllocation,
  getCoopExtendedCost,
  getCost,
  getExtendedCost,
} from "../helpers";
import { useCurrentOrderSet } from "../orderSet/data/CurrentOrderSetContext";

const OrderSetVariantsOverview = () => {
  const { orderSet, orders, orderSetVariants, isCoopOrderSet } =
    useCurrentOrderSet();

  const groupedOrderVariants: Record<string, OrderVariant[]> = _(orders ?? [])
    .flatMap("orderVariants")
    .filter((ov) => ov.qty > 0)
    .groupBy("variant.id")
    .value();

  const estLabel = (label: string) =>
    isCoopOrderSet ? label : `Est. ${label}`;

  if (!orderSet) return <CircularProgress />;

  return (
    <Paper>
      <SmartTable
        rows={orderSetVariants.filter((osv) => (osv.totalQty ?? 0) > 0)}
        columns={[
          {
            id: "variant.variantSku",
            label: "Item Number",
            render: (sku, osv) => (
              <div className="flex gap-1">
                {sku}
                {osv.variant.item.includeBeacon && <BeaconIcon />}
              </div>
            ),
          },
          { id: "variant.item.type", label: "Item Type" },
          {
            id: "estimatedCost",
            label: estLabel("Cost"),
            align: "right",
            render: (c) => (
              <CompareCosts cost={getCost(c, isCoopOrderSet)} comparedTo={c} />
            ),
          },
          {
            id: "_totalQty",
            label: "Quantity",
            align: "right",
            render: (v, osv) =>
              _.sumBy(groupedOrderVariants[osv.variant.id], "qty"),
          },
          {
            id: "_extendedCost",
            label: estLabel("Extended Cost"),
            align: "right",
            render: (v, osv) => {
              const ovs = groupedOrderVariants[osv.variant.id];
              const extendedCost = _.sumBy(ovs, getExtendedCost);
              return (
                <CompareCosts
                  cost={
                    isCoopOrderSet
                      ? _.sumBy(ovs, (ov) =>
                          getCoopExtendedCost(ov, ov.orderOrderingType)
                        )
                      : extendedCost
                  }
                  comparedTo={extendedCost}
                />
              );
            },
          },
          {
            id: "inMarketDate",
            label: "In Market Date",
            render: (v, osv) =>
              formatDateString(v ?? osv.estimatedInMarketDate),
          },
        ]}
        collapsibleContent={(osv) => (
          <div className="max-w-fit">
            <h4 className="text-neutral-600 mb-2">Item Allocations</h4>
            <Paper>
              <SmartTable
                rows={groupedOrderVariants[osv.variant.id] ?? []}
                columns={[
                  { id: "order.id", label: "Order #" },
                  {
                    id: "order.address.abn",
                    label: "Distributor ABN",
                  },
                  {
                    id: "order.address.name",
                    label: "Distributor / Name",
                    render: (value, ov) =>
                      ov.orderOrderingType === "to-inventory" ? (
                        <>
                          <Tags.Restock />
                          {displayToInventoryAllocation(ov.order as Order)}
                        </>
                      ) : (
                        value
                      ),
                  },
                  { id: "qty", label: "Total Allocated" },
                ]}
              />
            </Paper>
          </div>
        )}
      />
    </Paper>
  );
};

export default OrderSetVariantsOverview;
